import React, { useEffect, useState } from "react";
import { CalendarClock, Check, ArrowRight, Phone, Mail } from "lucide-react";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";
import { useDocMeta } from "../../lib/useDocMeta";
import { track, EVENT } from "../../lib/publicAnalytics";
import { toast } from "sonner";

const REASONS = [
  "Walk through the Sample Report with your team",
  "Map GovBidIQ to your set-aside or sub strategy",
  "Discuss pricing for >5 analyses per month",
  "Get a recommendation on capture / proposal workflow fit",
];

export default function StrategyCall() {
  const [form, setForm] = useState({ name: "", email: "", company: "", role: "", notes: "" });
  const [submitted, setSubmitted] = useState(false);
  const [busy, setBusy] = useState(false);

  useDocMeta({
    title: "Request a Strategy Call · GovBidIQ",
    description: "Book a 25-minute strategy call with the GovBidIQ team.",
    path: "/strategy-call",
  });
  useEffect(() => { track(EVENT.STRATEGY_CALL_CLICKED, {}); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.email.trim() || !form.name.trim()) {
      toast.error("Name and email are required.");
      return;
    }
    setBusy(true);
    try {
      // Reuses the existing /api/leads upsert. No new endpoint added.
      await api.post("/leads", {
        email: form.email.trim().toLowerCase(),
        name: form.name.trim(),
        company: form.company.trim(),
        source: "strategy_call",
        metadata: { role: form.role, notes: form.notes },
      });
      track("public_strategy_call_submitted", { has_notes: !!form.notes });
      setSubmitted(true);
    } catch {
      toast.error("Could not submit — please try again or email us directly.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-[1100px] mx-auto px-6 py-14">
      <div className="font-mono text-[10px] tracking-widest uppercase text-sky-400 mb-3">/ Strategy Call</div>
      <h1 className="font-display font-black text-4xl sm:text-5xl text-slate-100 leading-tight mb-3">Talk to a human.</h1>
      <p className="text-slate-400 text-lg max-w-2xl leading-relaxed mb-10">
        A 25-minute conversation. No pitch deck. We'll look at your pipeline together and decide if GovBidIQ moves the needle.
      </p>

      <div className="grid md:grid-cols-5 gap-px bg-[var(--border-soft)]">
        <div className="bg-[var(--bg-secondary)] p-6 md:col-span-2 space-y-4">
          <div className="font-mono text-[10px] tracking-widest uppercase text-sky-400">/ What we'll cover</div>
          <ul className="space-y-2">
            {REASONS.map((r) => (
              <li key={r} className="flex items-start gap-2 text-slate-200 text-sm leading-relaxed">
                <Check className="w-4 h-4 text-emerald-400 mt-0.5 shrink-0" strokeWidth={2.5} /> {r}
              </li>
            ))}
          </ul>
          <div className="pt-4 border-t border-[var(--border-soft)] space-y-2 text-sm">
            <div className="flex items-center gap-2 text-slate-400"><CalendarClock className="w-4 h-4 text-sky-400" /> 25 minutes</div>
            <div className="flex items-center gap-2 text-slate-400"><Phone className="w-4 h-4 text-sky-400" /> Zoom / Google Meet / Teams</div>
            <a href="mailto:BidNoBidIQ@aol.com" data-testid="strategy-call-email" className="flex items-center gap-2 text-slate-400 hover:text-sky-300 transition-colors"><Mail className="w-4 h-4 text-sky-400" /> BidNoBidIQ@aol.com</a>
          </div>
        </div>

        <div className="bg-[var(--bg-secondary)] p-6 md:col-span-3">
          {submitted ? (
            <div className="py-12 text-center">
              <div className="w-12 h-12 mx-auto border border-emerald-500/60 bg-emerald-500/10 flex items-center justify-center mb-4">
                <Check className="w-6 h-6 text-emerald-400" strokeWidth={2.5} />
              </div>
              <div className="font-display font-bold text-slate-100 text-xl mb-2">Request received.</div>
              <p className="text-slate-400 text-sm max-w-md mx-auto leading-relaxed mb-6">
                We'll reach out within one business day to schedule. While you wait, take the Product Tour or view a Sample Report.
              </p>
              <div className="flex flex-wrap justify-center gap-2">
                <Link to="/tour" data-testid="strategy-success-tour" className="inline-flex items-center gap-2 border border-sky-500/60 hover:bg-sky-500/10 text-sky-300 font-mono text-[11px] tracking-widest uppercase px-4 py-2.5">Product Tour <ArrowRight className="w-3 h-3" /></Link>
                <Link to="/sample" data-testid="strategy-success-sample" className="inline-flex items-center gap-2 border border-sky-500/60 hover:bg-sky-500/10 text-sky-300 font-mono text-[11px] tracking-widest uppercase px-4 py-2.5">Sample Report <ArrowRight className="w-3 h-3" /></Link>
              </div>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-3">
              <div className="font-mono text-[10px] tracking-widest uppercase text-sky-400 mb-3">/ Request a Call</div>
              <Field label="Name *" v={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="strategy-name" />
              <Field label="Work Email *" v={form.email} onChange={(v) => setForm({ ...form, email: v })} testid="strategy-email" type="email" />
              <Field label="Company" v={form.company} onChange={(v) => setForm({ ...form, company: v })} testid="strategy-company" />
              <Field label="Your Role" v={form.role} onChange={(v) => setForm({ ...form, role: v })} testid="strategy-role" />
              <label className="block">
                <div className="font-mono text-[10px] tracking-widest uppercase text-slate-500 mb-1">What would make this call worthwhile?</div>
                <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={4} data-testid="strategy-notes" className="w-full bg-slate-950 border border-slate-700 focus:border-sky-500 outline-none text-slate-100 text-sm px-3 py-2 font-mono" />
              </label>
              <button type="submit" disabled={busy} data-testid="strategy-submit" className="w-full bg-sky-500 hover:bg-sky-400 disabled:opacity-50 text-slate-950 font-mono text-[11px] tracking-[0.2em] uppercase font-bold px-5 py-3 mt-2">
                {busy ? "Sending…" : "Request Strategy Call"}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

function Field({ label, v, onChange, testid, type = "text" }) {
  return (
    <label className="block">
      <div className="font-mono text-[10px] tracking-widest uppercase text-slate-500 mb-1">{label}</div>
      <input type={type} value={v} onChange={(e) => onChange(e.target.value)} data-testid={testid} className="w-full bg-slate-950 border border-slate-700 focus:border-sky-500 outline-none text-slate-100 text-sm px-3 py-2 font-mono" />
    </label>
  );
}
